import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import Flatpickr from "flatpickr";
import { Spanish } from "flatpickr/dist/l10n/es.js";
import "flatpickr/dist/themes/material_green.css";
import Header from "../components/Header";
import ScrollToTop from "../components/ScrollTop";
import PhoneLoader from "../components/loader/PhoneLoader";
import Footer from "../components/Footer";
import "../Dashboard.css";
import "../styles/Receivables.css";
const api = axios.create({
  baseURL: (process.env.REACT_APP_API || "").replace(/\/$/, ""),
});
api.interceptors.request.use((c) => {
  c.headers.Authorization = `Bearer ${localStorage.getItem("token") || ""}`;
  return c;
});
// Fecha civil de Lima en ISO (YYYY-MM-DD), independiente del formato regional de Node/navegador.
const limaTodayISO = () => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Lima', year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(new Date());
  const fields = Object.fromEntries(parts.filter(p => p.type !== 'literal').map(p => [p.type, p.value]));
  return `${fields.year}-${fields.month}-${fields.day}`;
};
const money = (v) => `S/ ${Number(v || 0).toFixed(2)}`;
const date = (v) => {
  const s = String(v || "").slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(s)
    ? `${s.slice(8)}/${s.slice(5, 7)}/${s.slice(0, 4)}`
    : s;
};
const errorText = (e) =>
  e.response?.status === 404
    ? "Ruta no encontrada: verifica que el backend actualizado esté desplegado y REACT_APP_API sea correcto."
    : e.response?.data?.error || e.response?.data?.message || e.message;
export default function Receivables() {
  const [items, setItems] = useState([]),
    [detail, setDetail] = useState(null),
    [query, setQuery] = useState(""),
    [periodMode, setPeriodMode] = useState("day"),
    [periodStart, setPeriodStart] = useState(() => limaTodayISO()),
    [periodEnd, setPeriodEnd] = useState(() => limaTodayISO()),
    [error, setError] = useState(""),
    [notice, setNotice] = useState(""),
    [loading, setLoading] = useState(false),
    [paying, setPaying] = useState(null),
    [confirmPayment, setConfirmPayment] = useState(false),
    [paymentBusy, setPaymentBusy] = useState(false),
    [importOpen, setImportOpen] = useState(false),
    [importStart, setImportStart] = useState(() => limaTodayISO()),
    [importEnd, setImportEnd] = useState(() => limaTodayISO()),
    [candidates, setCandidates] = useState([]),
    [selectedIds, setSelectedIds] = useState([]),
    [importBusy, setImportBusy] = useState(false),
    [payment, setPayment] = useState({
      amount: "",
      method: "Efectivo",
      reference: "",
    });
  const startPickerRef = useRef(null);
  const endPickerRef = useRef(null);
  const formatSale = (value) => {
    if (!value) return "—";
    const raw = String(value);
    const parsed = new Date(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?$/.test(raw) ? `${raw}Z` : raw);
    if (Number.isNaN(parsed.getTime())) return raw.replace("T", " ").slice(0,16);
    return new Intl.DateTimeFormat("es-PE", {timeZone:"America/Lima",day:"2-digit",month:"2-digit",year:"numeric",hour:"2-digit",minute:"2-digit",hour12:false}).format(parsed);
  };
  // Flatpickr conserva fechas civiles YYYY-MM-DD, sin conversión implícita a UTC.
  useEffect(() => {
    const instances = [];
    const options = {
      locale: Spanish, dateFormat: "Y-m-d", allowInput: false,
      maxDate: limaTodayISO(), disableMobile: true,
    };
    if (startPickerRef.current) instances.push(Flatpickr(startPickerRef.current, {
      ...options, defaultDate: periodStart,
      onChange: (_, value) => {
        if (!value) return;
        setPeriodStart(value);
        if (periodMode === "day") {
          setPeriodEnd(value);
          load(query, value, value);
        } else {
          const end = value > periodEnd ? value : periodEnd;
          if (end !== periodEnd) setPeriodEnd(end);
          load(query, value, end);
        }
      },
    }));
    if (periodMode === "range" && endPickerRef.current) instances.push(Flatpickr(endPickerRef.current, {
      ...options, defaultDate: periodEnd, minDate: periodStart,
      onChange: (_, value) => {
        if (!value) return;
        setPeriodEnd(value);
        if (value >= periodStart) load(query, periodStart, value);
      },
    }));
    return () => instances.forEach(instance => instance.destroy());
  }, [periodMode, periodStart, periodEnd]);

  const importStartPickerRef = useRef(null);
  const importEndPickerRef = useRef(null);
  useEffect(() => {
    const instances = [];
    const opts = {locale: Spanish, dateFormat: "Y-m-d", maxDate: limaTodayISO(), disableMobile: true, allowInput: false};
    if (importOpen) {
      if (importStartPickerRef.current) instances.push(Flatpickr(importStartPickerRef.current, {
        ...opts, defaultDate: importStart,
        onChange: (_, value) => { if (value) {setImportStart(value); if (value > importEnd) setImportEnd(value);} },
      }));
      if (importEndPickerRef.current) instances.push(Flatpickr(importEndPickerRef.current, {
        ...opts, defaultDate: importEnd, minDate: importStart,
        onChange: (_, value) => {if (value) setImportEnd(value);},
      }));
    }
    return () => instances.forEach(instance => instance.destroy());
  }, [importOpen, importStart, importEnd]);

  const load = async (q = "", start = periodStart, end = periodMode === "day" ? periodStart : periodEnd) => {
    if (!start || !end || start > end || end > limaTodayISO()) { setError("Seleccione un período válido hasta hoy."); return; }
    setLoading(true);
    setError("");
    try {
      const r = await api.get("/receivables", { params: { q, start_date: start, end_date: end } });
      setItems(r.data);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load("", limaTodayISO(), limaTodayISO());
  }, []);
  const open = async (id) => {
    try {
      const r = await api.get(`/receivables/${id}`);
      setDetail(r.data);
      setError("");
    } catch (e) {
      setError(errorText(e));
    }
  };
  const today = limaTodayISO();
  const fetchCandidates = async () => {
    if (!importStart || !importEnd || importStart > importEnd || importEnd > today) {setError('Seleccione un rango válido hasta hoy.');return;}
    setImportBusy(true);setError('');setSelectedIds([]);
    try {const r=await api.get('/receivables/import-candidates',{params:{start_date:importStart,end_date:importEnd}});setCandidates(r.data)}
    catch(e){setError(errorText(e))}finally{setImportBusy(false)}
  };
  const sync = async () => {
    if(!selectedIds.length)return;
    setImportBusy(true);setError('');
    try {const r=await api.post('/receivables/sync',{start_date:importStart,end_date:importEnd,register_ids:selectedIds});
      setNotice(`${r.data.created} contratos creados; ${r.data.skipped} omitidos; ${r.data.failed?.length||0} errores.`);
      await load(query);if(!r.data.failed?.length)setImportOpen(false);else await fetchCandidates();
    }catch(e){setError(errorText(e))}finally{setImportBusy(false)}
  };
  const frequency = async (value) => {
    if (
      !window.confirm(
        "¿Reprogramar las cuotas? No se permite si existen pagos.",
      )
    )
      return;
    try {
      await api.post(`/receivables/${detail.contract.id}/frequency`, {
        frequency: value,
      });
      await open(detail.contract.id);
      await load(query);
    } catch (e) {
      setError(errorText(e));
    }
  };
  const pay = async (e) => {
    e.preventDefault();
    if (!confirmPayment) { setConfirmPayment(true); return; }
    if (paymentBusy) return;
    setPaymentBusy(true);
    try {
      const emission = await api.post(`/receivables/installments/${paying.id}/pay-and-issue`, {
        ...payment,
        amount: Number(payment.amount),
      });
      setPaying(null);
      setConfirmPayment(false);
      await open(detail.contract.id);
      await load(query);
      setNotice(`Pago registrado. SUNAT: ${emission.data.status || "EN PROCESO"}. Documento: ${emission.data.fullNumber || "pendiente"}.`);
    } catch (e) {
      setError(errorText(e) + (e.response?.data?.paymentId ? ` El pago #${e.response.data.paymentId} ya fue registrado: NO vuelva a cobrar. Consulte su estado SUNAT.` : ""));
      if(e.response?.data?.paymentId){setPaying(null);setConfirmPayment(false);await open(detail.contract.id);await load(query)}
    } finally { setPaymentBusy(false); }
  };
  const financed = items.reduce(
      (a, c) => a + Number(c.financed_amount || 0),
      0,
    ),
    paid = items.reduce((a, c) => a + Number(c.total_paid || 0), 0);
  return (
    <>
      <div>
        {loading && <PhoneLoader />}
        <ScrollToTop />
        <Header />

        <section className="sectionBody receivablePage">
          <div className="containerCustom containerBody">
            <div className="receivableHero">
              <div>
                <span className="receivableEyebrow">
                  Administración financiera
                </span>
                <h1>Cuotas por Cobrar</h1>
                <p>Módulo independiente</p>
              </div>
              <button
                className="receivablePrimary"
                disabled={loading}
                onClick={() => {setImportOpen(true);setCandidates([]);setSelectedIds([]);setError("");}}
              >
                Importar ventas confirmadas
              </button>
            </div>

            <section className="receivablePeriod" aria-label="Período de consulta">
              <div className="receivablePeriodHeading"><span className="receivablePeriodIcon">▦</span><div><strong>HISTORIAL · Período de consulta</strong><small>Contratos por fecha de venta</small></div></div>
              <div className="receivablePeriodModes" role="group" aria-label="Modalidad del período">
                <button type="button" className={periodMode === "day" ? "active" : ""} onClick={() => {setPeriodMode("day");load(query,periodStart,periodStart);}}>Un día</button>
                <button type="button" className={periodMode === "range" ? "active" : ""} onClick={() => {setPeriodMode("range");setPeriodEnd(periodStart);load(query,periodStart,periodStart);}}>Rango</button>
              </div>
              <div className="receivablePeriodDates">
                <label>{periodMode === "day" ? "Fecha seleccionada" : "Desde"}<input ref={startPickerRef} aria-label="Fecha inicial" type="text" readOnly /></label>
                {periodMode === "range" && <label>Hasta<input ref={endPickerRef} aria-label="Fecha final" type="text" readOnly /></label>}
              </div>
              <div className="receivablePeriodShowing"><small>MOSTRANDO</small><strong>{date(periodStart)}{periodMode === "range" ? ` – ${date(periodEnd)}` : ""}</strong></div>
              <button type="button" className="receivableToday" onClick={() => {const v=limaTodayISO();setPeriodMode("day");setPeriodStart(v);setPeriodEnd(v);load(query,v,v);}}>◷ Hoy</button>
            </section>
            <div className="receivableStats">
              <article>
                <span>Contratos</span>
                <strong>{items.length}</strong>
              </article>
              <article>
                <span>Total financiado</span>
                <strong>{money(financed)}</strong>
              </article>
              <article>
                <span>Saldo pendiente</span>
                <strong>{money(financed - paid)}</strong>
              </article>
            </div>
            {error && (
              <div className="receivableAlert error" role="alert">
                {error}
              </div>
            )}
            {notice && <div className="receivableAlert success">{notice}</div>}
            <div className="receivablePanel">
              <div className="receivablePanelHeader">
                <div>
                  <h2>Contratos registrados</h2>
                  <p>Consulta por cliente, DNI o IMEI.</p>
                </div>
                <form
                  className="receivableSearch"
                  onSubmit={(e) => {
                    e.preventDefault();
                    load(query);
                  }}
                >
                  <input
                    placeholder="Cliente, DNI o IMEI"
                    aria-label="Buscar"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                  />
                  <button>Buscar</button>
                </form>
              </div>
              <div className="receivableTableWrap">
                <table className="receivableTable">
                  <thead><tr>
                    <th scope="col">N.º</th><th scope="col">Cliente</th><th scope="col">Equipo / IMEI</th>
                    <th scope="col">Financiera</th><th scope="col">Asesor</th><th scope="col">Plazo</th>
                    <th scope="col">Fecha y hora de venta</th><th scope="col">Comprobante</th>
                    <th scope="col">Frecuencia</th><th scope="col">Fin de contrato</th>
                    <th scope="col">Saldo</th><th scope="col">Acción</th>
                  </tr>
                  </thead>
                  <tbody>
                    {items.length ? (
                      items.map((c,index) => (
                        <tr key={c.id}>
                          <td className="receivableIndex">{index + 1}</td>
                          <td className="receivableClient">
                            <strong>{c.client_name}</strong>
                            <small>DNI {c.dni}</small>
                          </td>
                          <td className="receivableEquipment">
                            <strong>{c.model}</strong>
                            <small>{c.imei}</small>
                          </td>
                          <td className="receivableFinance">{c.financiera || "—"}</td>
                          <td className="receivableAdvisor">{c.advisor || "No registrado"}</td>
                          <td className="receivableTerm">{c.contract_installments == null ? "—" : `${c.contract_installments} cuotas`}</td>
                          <td className="receivableSaleDate">{formatSale(c.sale_datetime)}</td>
                          <td className="receivableDocument">{c.document_number}</td>
                          <td>
                            <span className="receivableTag">{c.frequency}</span>
                          </td>
                          <td>{date(c.end_date)}</td>
                          <td>
                            {money(
                              Number(c.financed_amount) - Number(c.total_paid),
                            )}
                          </td>
                          <td>
                            <button
                              className="receivableSecondary"
                              onClick={() => open(c.id)}
                            >
                              Ver cuotas
                            </button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="12" className="receivableEmpty">
                          {loading
                            ? "Cargando…"
                            : "No hay contratos para mostrar."}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            {importOpen && (
              <div className="receivableOverlay" onMouseDown={e=>{if(e.target===e.currentTarget&&!importBusy)setImportOpen(false)}}>
                <section className="receivableModal receivableImportModal" role="dialog" aria-modal="true" aria-label="Importar ventas anteriores">
                  <header><div><span className="receivableEyebrow">Importación histórica</span><h2>Ventas a crédito no importadas</h2><p>Solo ventas confirmadas, excepto Efectiva.</p></div><button className="receivableClose" onClick={()=>setImportOpen(false)} disabled={importBusy} aria-label="Cerrar">×</button></header>
                  <div className="receivableModalBody">
                    <div className="receivableImportFilters">
                      <label>Desde<input ref={importStartPickerRef} type="text" readOnly aria-label="Fecha inicial de importación" /></label>
                      <label>Hasta<input ref={importEndPickerRef} type="text" readOnly aria-label="Fecha final de importación" /></label>
                      <button className="receivablePrimary" onClick={fetchCandidates} disabled={importBusy}>Consultar ventas</button>
                    </div>
                    <p className="receivableImportInfo">{candidates.filter(x=>x.eligible).length} elegibles de {candidates.length} resultados (máximo 500 por consulta). Seleccionadas: {selectedIds.length} (máximo 100).</p>
                    <div className="receivableTableWrap"><table className="receivableTable"><thead><tr><th>Elegir</th><th>Fecha</th><th>Cliente / DNI</th><th>Equipo / IMEI</th><th>Comprobante</th><th>Saldo</th><th>Validación</th></tr></thead><tbody>
                      {candidates.map(c=><tr key={c.id}><td><input type="checkbox" disabled={!c.eligible||(!selectedIds.includes(c.id)&&selectedIds.length>=100)} checked={selectedIds.includes(c.id)} onChange={e=>setSelectedIds(prev=>e.target.checked?[...prev,c.id]:prev.filter(id=>id!==c.id))}/></td><td>{date(c.issued_at)}</td><td>{c.client_name}<small>{c.dni}</small></td><td>{c.model}<small>{c.imei}</small></td><td>{c.document_number}</td><td>{c.financed_amount==null?'—':money(c.financed_amount)}</td><td>{c.eligible?'Elegible':c.reason}</td></tr>)}
                      {!candidates.length&&<tr><td colSpan="7" className="receivableEmpty">Seleccione las fechas y presione Consultar ventas.</td></tr>}
                    </tbody></table></div>
                    <div className="receivableActions receivableImportActions"><button className="receivableSecondary" disabled={importBusy} onClick={()=>setImportOpen(false)}>Cancelar</button><button className="receivablePrimary" disabled={importBusy||!selectedIds.length} onClick={sync}>Importar {selectedIds.length} venta(s)</button></div>
                  </div>
                </section>
              </div>
            )}
            {detail && (
              <div
                className="receivableOverlay"
                onMouseDown={(e) => {
                  if (e.target === e.currentTarget) setDetail(null);
                }}
              >
                <section
                  className="receivableModal"
                  role="dialog"
                  aria-modal="true"
                  aria-label="Detalle de cuotas"
                >
                  <header>
                    <div>
                      <span className="receivableEyebrow">
                        Cronograma del contrato
                      </span>
                      <h2>{detail.contract.client_name}</h2>
                      <p>
                        DNI {detail.contract.dni} · IMEI {detail.contract.imei}
                      </p>
                    </div>
                    <button
                      className="receivableClose"
                      onClick={() => setDetail(null)}
                    >
                      ×
                    </button>
                  </header>
                  <div className="receivableModalBody">
                    <div className="receivableFacts">
                      <div>
                        <span>Modelo</span>
                        <strong>{detail.contract.model}</strong>
                      </div>
                      <div>
                        <span>Comprobante original</span>
                        <strong>{detail.contract.document_number}</strong>
                      </div>
                      <div>
                        <span>Empresa emisora / RUC</span>
                        <strong>{detail.contract.issuer_name || "No disponible"}</strong>
                        <small>{detail.contract.issuer_ruc || ""}</small>
                      </div>
                      <div>
                        <span>Inicio / fin</span>
                        <strong>
                          {date(detail.contract.start_date)} –{" "}
                          {date(detail.contract.end_date)}
                        </strong>
                      </div>
                      <div>
                        <span>Financiado</span>
                        <strong>
                          {money(detail.contract.financed_amount)}
                        </strong>
                      </div>
                      <div>
                        <span>Frecuencia</span>
                        <select
                          value={detail.contract.frequency}
                          onChange={(e) => frequency(e.target.value)}
                        >
                          {[
                            "mensual",
                            "quincenal",
                            "catorcenal",
                            "semanal",
                          ].map((f) => (
                            <option key={f} value={f}>
                              {f}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="receivableTableWrap">
                      <table className="receivableTable">
                        <thead>
                          <tr>
                            <th>N.º</th>
                            <th>Vencimiento</th>
                            <th>Importe</th>
                            <th>Pagado</th>
                            <th>Estado</th>
                            <th>Comprobante cuota / SUNAT</th>
                            <th>Acción</th>
                          </tr>
                        </thead>
                        <tbody>
                          {detail.installments.map((i) => {
                            const remaining = Number(i.amount) - Number(i.paid);
                            return (
                              <tr key={i.id}>
                                <td>{i.number}</td>
                                <td>{date(i.due_date)}</td>
                                <td>{money(i.amount)}</td>
                                <td>{money(i.paid)}</td>
                                <td>
                                  <span
                                    className={
                                      "receivableTag " +
                                      (remaining <= 0 ? "paid" : "")
                                    }
                                  >
                                    {remaining <= 0
                                      ? "Pagada"
                                      : Number(i.paid) > 0
                                        ? "Parcial"
                                        : "Pendiente"}
                                  </span>
                                </td>
                                <td>
                                  {i.issued_documents ? <><strong>{i.issued_documents}</strong><small style={{display:"block"}}>SUNAT: {i.issued_statuses || "Pendiente"}</small></> : <span className="receivableNoDocument">Sin comprobante emitido</span>}
                                </td>
                                <td>
                                  <button
                                    className="receivableSecondary"
                                    disabled={remaining <= 0}
                                    onClick={() => {
                                      setPaying(i);
                                      setPayment({
                                        amount: remaining.toFixed(2),
                                        method: "Efectivo",
                                        reference: "",
                                      });
                                    }}
                                  >
                                    Registrar pago
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </section>
              </div>
            )}
            {paying && confirmPayment && (
              <div className="receivableOverlay paymentLayer" style={{zIndex: 10002}}>
                <div className="receivablePayment" role="dialog" aria-modal="true" aria-label="Confirmar pago">
                  <h3>Confirmar pago de cuota</h3>
                  <p>Cliente: {detail?.contract?.client_name || "—"}</p>
                  <p>Comprobante madre: <strong>{detail?.contract?.document_number || "—"}</strong></p>
                  <p>Empresa emisora: <strong>{detail?.contract?.issuer_name || "—"}</strong> · RUC {detail?.contract?.issuer_ruc || "—"}</p>
                  <p>Cuota N.º <strong>{paying.number}</strong></p>
                  <p>Importe: <strong>{money(payment.amount)}</strong></p>
                  <p>Método: {payment.method}</p>
                  <p>Esta acción registra el pago y solicita la emisión electrónica a SUNAT. El resultado depende de la respuesta del servicio.</p>
                  <div className="receivableActions">
                    <button type="button" className="receivableSecondary" disabled={paymentBusy} onClick={()=>setConfirmPayment(false)}>Volver</button>
                    <button type="button" className="receivablePrimary" disabled={paymentBusy} onClick={()=>pay({preventDefault:()=>{}})}>{paymentBusy ? "Registrando..." : "Confirmar pago"}</button>
                  </div>
                </div>
              </div>
            )}
            {paying && !confirmPayment && (
              <div className="receivableOverlay paymentLayer">
                <form className="receivablePayment" onSubmit={pay}>
                  <h3>Pago de cuota {paying.number}</h3>
                  <p>Rellena el formulario.</p>
                  <label>
                    Monto
                    <input
                      type="number"
                      required
                      min="0.01"
                      step="0.01"
                      max={(
                        Number(paying.amount) - Number(paying.paid)
                      ).toFixed(2)}
                      value={payment.amount}
                      onChange={(e) =>
                        setPayment({ ...payment, amount: e.target.value })
                      }
                    />
                  </label>
                  <label>
                    Método
                    <select
                      value={payment.method}
                      onChange={(e) =>
                        setPayment({ ...payment, method: e.target.value })
                      }
                    >
                      {[
                        "Efectivo",
                        "Yape",
                        "Transferencia",
                        "Tarjeta",
                        "Otro",
                      ].map((x) => (
                        <option key={x}>{x}</option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Referencia
                    <input
                      maxLength="100"
                      value={payment.reference}
                      onChange={(e) =>
                        setPayment({ ...payment, reference: e.target.value })
                      }
                    />
                  </label>
                  <div className="receivableActions">
                    <button
                      type="button"
                      className="receivableSecondary"
                      onClick={() => {setPaying(null);setConfirmPayment(false);}}
                    >
                      Cancelar
                    </button>
                    <button className="receivablePrimary">Guardar pago</button>
                  </div>
                </form>
              </div>
            )}
          </div>
        </section>
        <Footer />
      </div>
    </>
  );
}
